import React, { useEffect, useState } from "react";
import styles from './ExportLogReports.module.scss';
import PageTitle from "../../../components/pageTitle/PageTitle";
import Dropdown from "../../../components/dropdown/Dropdown";
import Button from "../../../components/button/Button";
import CustomDatePicker from "../../../components/customDatePicker/CustomDatePicker";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { format } from "date-fns";

const ExportLogReports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [studentList, setStudentList] = useState([]);
  const [studentId, setStudentId] = useState(null);
  const [clinicalSiteList, setClinicalSiteList] = useState([]);
  const [clinicalSiteId, setClinicalSiteId] = useState(null);
  const [programYearList, setProgramYearList] = useState([]);
  const [programYearId, setProgramYearId] = useState(null);
  const [instructorList, setInstructorList] = useState([]);
  const [instructorId, setInstructorId] = useState(null);
  const [dateOfCase, setDateOfCase] = useState(null);
  const navigate = useNavigate();
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`users/students`)
      .then((response) => {
        if(response.data?.students) {
          setStudentList(response.data?.students.map((item) => ({
            value: item.id,
            label: `${item.firstName} ${item.lastName}`
          })));
        }
      })
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`clinical-sites/findAll`)
      .then((response) => {
        setClinicalSiteList(response.data.map((item) => ({
          value: item.id,
          label: item.name
        })));
      })
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`years/details`)
      .then((response) => {
        setProgramYearList(response.data.map((item) => ({
          value: item.year.id,
          label: item.year.year
        })));
      })
      .catch((error) => { showToast(error.message, 'error'); })
    };

    fetchData();
  }, [showToast]);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`users/instructors`)
      .then((response) => {
        if(response.data?.instructors) {
          setInstructorList(response.data.instructors.map((item) => ({
            value: item.id,
            label: `${item.firstName} ${item.lastName}`
          })));
        }
      })
    };

    fetchData();
  }, []);

  const hanldeSubmit = async () => {
    setIsLoading(true);
    const params = {};
    if(studentId) {
      params.studentId = studentId;
    }
    if(clinicalSiteId) {
      params.clinicalSiteId = clinicalSiteId;
    }
    if(programYearId) {
      params.yearId = programYearId;
    }
    if(instructorId) {
      params.instructorId = instructorId;
    }
    if(dateOfCase) {
      params.dateOfCase = format(dateOfCase, 'yyyy-MM-dd');
    }
    await axiosClient.get('case-log/download-report', {
      params,
      responseType: 'blob',
    })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response]));
  
      // Create an anchor element
      const link = document.createElement('a');
      
      // Set the download attribute with the filename
      link.href = url;
      link.setAttribute('download', 'case-log-report.xlsx'); // Replace with the desired filename and extension
      
      // Append the link to the DOM and click it to trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up by removing the link element
      document.body.removeChild(link);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle
          label="Log Report"
        />
      </div>
      <div className={styles.container}>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            hanldeSubmit();
          }}
        >
          <h3>Export Log Reports</h3>
          <div className={styles.row}>
            <div className={styles.column50}>
              <Dropdown
                label="Student"
                options={studentList}
                value={studentId}
                onChange={(selected) => setStudentId(selected)}
                searchable={true}
              />
            </div>
            <div className={styles.column50}>
              <Dropdown
                label="Clinical Site"
                options={clinicalSiteList}
                value={clinicalSiteId}
                onChange={(selected) => setClinicalSiteId(selected)}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.column50}>
              <Dropdown
                label="Program Year"
                options={programYearList}
                value={programYearId}
                onChange={(selected) => setProgramYearId(selected)}
              />
            </div>
            <div className={styles.column50}>
              <Dropdown
                label="Instructor"
                options={instructorList}
                value={instructorId}
                onChange={(selected) => setInstructorId(selected)}
                searchable={true}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.column50}>
              <CustomDatePicker
                label="Date of Case"
                date={dateOfCase}
                onChange={(date) => setDateOfCase(date)}
                showIcon={true}
                maxDate={new Date()}
              />
            </div>
          </div>
          <div className={styles.actions}>
            <Button
              label="Cancel"
              variant="white"
              size="medium"
              onClick={() => navigate('/log-reports')}
            />
            <Button
              label="Export Report"
              variant="primary"
              size="medium"
              type="submit"
              disabled={!(studentId || clinicalSiteId || programYearId || instructorId || dateOfCase)}
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default ExportLogReports;